import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Message } from 'semantic-ui-react';
import moment from 'moment';
import PINInput from 'components/common/PINInput';
import approveWalletTransaction, {
  resetApproveWalletTransaction,
} from 'redux/actions/wallets/approveWalletTransaction';
import getCurrentOrg from 'helpers/getCurrentOrg';
import formatNumber from 'helpers/formatNumber';
import SimpleModal from '../SimpleModal';
import './index.scss';

const transactionTypeLabels = {
  load_wallet_admin_approval: 'Load wallet',
  user_wallet_top_up_admin_approval: 'User wallet top-up',
  group_wallet_top_up_admin_approval: 'Group wallet top-up',
};

const ApproveWalletTransactionModal = ({
  open,
  onClose,
  requestId,
  notification,
  canApprove,
}) => {
  const dispatch = useDispatch();
  const [pin, setPin] = useState('');
  const [action, setAction] = useState('');
  const currentOrg = getCurrentOrg();

  const { loading, error } = useSelector(
    ({ wallets: { approveWalletTransaction } }) =>
      approveWalletTransaction,
  );

  const payload = notification?.request?.payload?.[0];

  const handleClose = () => {
    setPin('');
    setAction('');
    resetApproveWalletTransaction()(dispatch);
    onClose();
  };

  const handlePinChange = (_, { value }) => {
    setPin(value);
  };

  const handleAction = action => {
    if (!pin) return;

    setAction(action);

    approveWalletTransaction({
      organizationId: currentOrg?.id,
      requestId,
      action,
      data: { password: pin },
      onSuccess: () => {
        handleClose();
      },
    })(dispatch);
  };

  return (
    <SimpleModal
      title={
        canApprove
          ? 'Approve wallet transaction'
          : 'Wallet transaction details'
      }
      open={open}
      onClose={handleClose}
      className="approve-wallet-transaction-modal"
    >
      <div className="approve-wallet-transaction-modal__body">
        {notification && (
          <div className="approve-wallet-transaction-modal__details">
            {transactionTypeLabels[notification.type] && (
              <div className="approve-wallet-transaction-modal__details__row">
                <span className="approve-wallet-transaction-modal__details__row__label">
                  Transaction
                </span>
                <span className="approve-wallet-transaction-modal__details__row__value">
                  {transactionTypeLabels[notification.type]}
                </span>
              </div>
            )}
            {notification?.initiator && (
              <div className="approve-wallet-transaction-modal__details__row">
                <span className="approve-wallet-transaction-modal__details__row__label">
                  Initiated by
                </span>
                <span className="approve-wallet-transaction-modal__details__row__value">
                  {notification.initiator.first_name}{' '}
                  {notification.initiator.last_name}
                </span>
              </div>
            )}
            {payload?.wallet_id && (
              <div className="approve-wallet-transaction-modal__details__row">
                <span className="approve-wallet-transaction-modal__details__row__label">
                  Wallet ID
                </span>
                <span className="approve-wallet-transaction-modal__details__row__value">
                  {payload.wallet_id}
                </span>
              </div>
            )}
            {payload?.phone_number && (
              <div className="approve-wallet-transaction-modal__details__row">
                <span className="approve-wallet-transaction-modal__details__row__label">
                  Phone number
                </span>
                <span className="approve-wallet-transaction-modal__details__row__value">
                  {payload.phone_number}
                </span>
              </div>
            )}
            <div className="approve-wallet-transaction-modal__details__row">
              <span className="approve-wallet-transaction-modal__details__row__label">
                Amount
              </span>
              <span className="approve-wallet-transaction-modal__details__row__value approve-wallet-transaction-modal__details__row__value--amount">
                Rwf {formatNumber(notification?.request?.amount)}
              </span>
            </div>
            {payload?.comment && (
              <div className="approve-wallet-transaction-modal__details__row">
                <span className="approve-wallet-transaction-modal__details__row__label">
                  Comment
                </span>
                <span className="approve-wallet-transaction-modal__details__row__value">
                  {payload.comment}
                </span>
              </div>
            )}
            <div className="approve-wallet-transaction-modal__details__row">
              <span className="approve-wallet-transaction-modal__details__row__label">
                Requested on
              </span>
              <span className="approve-wallet-transaction-modal__details__row__value">
                {moment(notification.created_at).format(
                  'MMM DD, YYYY [at] h:mm A',
                )}
              </span>
            </div>
          </div>
        )}

        {canApprove && (
          <>
            <p className="approve-wallet-transaction-modal__warning">
              Approving this wallet transaction is permanent and
              cannot be reversed
            </p>

            <Form>
              <Form.Input label="Provide PIN to approve transaction">
                <PINInput
                  fluid
                  label="Provide PIN to approve transaction"
                  name="password"
                  onInputChange={handlePinChange}
                  onChange={handlePinChange}
                  error={error}
                />
              </Form.Input>
            </Form>

            {error && error.message && (
              <Message negative>{error.message}</Message>
            )}

            <div className="approve-wallet-transaction-modal__actions">
              <Button
                className="approve-wallet-transaction-modal__actions__decline"
                content="Decline"
                loading={loading && action !== 'accept'}
                disabled={loading || !pin}
                onClick={() => handleAction('reject')}
              />
              <Button
                color="blue"
                content="Approve"
                loading={loading && action === 'accept'}
                disabled={loading || !pin}
                onClick={() => handleAction('accept')}
              />
            </div>
          </>
        )}
      </div>
    </SimpleModal>
  );
};

ApproveWalletTransactionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  notification: PropTypes.objectOf(Object),
  canApprove: PropTypes.bool,
};

ApproveWalletTransactionModal.defaultProps = {
  notification: null,
  canApprove: true,
};

export default ApproveWalletTransactionModal;
